import React, { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CalendarBlank, Plus, Trash, FloppyDisk, X } from "@phosphor-icons/react";

/**
 * Inline editor for a registration's instalment schedule. The sum of instalment
 * amounts must equal the total payment_amount on the registration.
 * Used inside PaymentPanel; surfaces only when admin clicks "Configure instalments".
 */
export default function InstallmentEditor({ registration, onClose, onSaved }) {
  const total = Number(registration.payment_amount || 0);
  const initial = useMemo(() => {
    const existing = registration.installments || [];
    if (existing.length > 0) {
      return existing.map((i) => ({ amount: Number(i.amount), due_date: i.due_date }));
    }
    return [{ amount: total, due_date: new Date().toISOString().slice(0, 10) }];
  }, [registration.installments, total]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);

  const sum = items.reduce((a, b) => a + Number(b.amount || 0), 0);
  const balanced = Math.abs(sum - total) < 0.01;

  const addRow = () => {
    const lastDate = items.length ? new Date(items[items.length - 1].due_date) : new Date();
    lastDate.setMonth(lastDate.getMonth() + 1);
    setItems([...items, { amount: 0, due_date: lastDate.toISOString().slice(0, 10) }]);
  };

  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const update = (idx, k, v) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  };

  const splitEqually = () => {
    const n = items.length || 1;
    const each = Math.round((total / n) * 100) / 100;
    const remainder = Math.round((total - each * (n - 1)) * 100) / 100;
    setItems(items.map((it, i) => ({ ...it, amount: i === n - 1 ? remainder : each })));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/registrations/${registration.id}/installments`, {
        installments: items.map((i) => ({ amount: Number(i.amount), due_date: i.due_date })),
      });
      toast.success(`Schedule set · ${data.installments.length} instalment${data.installments.length === 1 ? "" : "s"}`);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-900 bg-white p-5 sms-fade-up" data-testid="installment-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarBlank size={18} weight="duotone" className="text-[var(--sms-primary)]" />
          <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Instalment schedule</div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="installment-close">
          <X size={16} weight="bold" />
        </button>
      </div>

      <div className="text-xs text-slate-500 mb-3">
        Total: <span className="font-mono-sms text-slate-900">₹ {total.toLocaleString("en-IN")}</span>
        {" · "}
        Sum: <span className={`font-mono-sms ${balanced ? "text-[var(--sms-success)]" : "text-[var(--sms-danger)]"}`}>₹ {sum.toLocaleString("en-IN")}</span>
      </div>

      <div className="space-y-2 mb-3">
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`installment-row-${idx}`}>
            <div className="col-span-1 font-mono-sms text-xs text-slate-400 text-center">#{idx + 1}</div>
            <div className="col-span-5">
              <input
                type="number" min={0} step="0.01"
                className="sms-input"
                placeholder="Amount"
                data-testid={`installment-amount-${idx}`}
                value={it.amount}
                onChange={(e) => update(idx, "amount", e.target.value)}
              />
            </div>
            <div className="col-span-5">
              <input
                type="date"
                className="sms-input"
                data-testid={`installment-due-${idx}`}
                value={it.due_date}
                onChange={(e) => update(idx, "due_date", e.target.value)}
              />
            </div>
            <div className="col-span-1 text-right">
              <button onClick={() => remove(idx)} disabled={items.length === 1} data-testid={`installment-remove-${idx}`} className="text-slate-400 hover:text-[var(--sms-danger)] disabled:opacity-30">
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={addRow} data-testid="installment-add-row" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
          <Plus size={12} weight="bold" /> Add instalment
        </button>
        <button onClick={splitEqually} data-testid="installment-split-equal" className="sms-btn-secondary text-xs">
          Split equally
        </button>
        <button onClick={save} disabled={!balanced || saving} data-testid="installment-save" className="sms-btn-primary text-xs inline-flex items-center gap-1 ml-auto">
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save schedule"}
        </button>
      </div>
    </div>
  );
}
